import {useChangeLocale} from '@common/locale-switcher/change-locale';
import {useBootstrapDataStore} from '@ui/bootstrap-data/bootstrap-data-store';
import {Button} from '@ui/buttons/button';
import {message} from '@ui/i18n/message';
import {useSelectedLocale} from '@ui/i18n/selected-locale';
import {useTrans} from '@ui/i18n/use-trans';
import {KeyboardArrowDownIcon} from '@ui/icons/material/KeyboardArrowDown';
import {LanguageIcon} from '@ui/icons/material/Language';
import {Menu, MenuItem, MenuTrigger} from '@ui/menu/menu-trigger';
import {useSettings} from '@ui/settings/use-settings';

interface LocaleSwitcherProps {
  appearance?: 'default' | 'auth';
}

export function LocaleSwitcher({appearance = 'default'}: LocaleSwitcherProps) {
  const {localeCode, localeName} = useSelectedLocale();
  const changeLocale = useChangeLocale();
  const siteLocales = useBootstrapDataStore(s => s.data.i18n.locales);
  const {i18n} = useSettings();
  const {trans} = useTrans();

  if (!siteLocales || !localeCode || !i18n.enable) return null;

  const isAuthAppearance = appearance === 'auth';
  const trigger = isAuthAppearance ? (
    <Button
      aria-label={trans(message('Change language'))}
      disabled={changeLocale.isPending}
      className="auth-locale-trigger gap-8 capitalize"
      color="elevated"
      variant="outline"
      radius="rounded-panel"
      shadow="shadow-sm"
      sizeClassName="h-44 w-44 p-0 sm:h-40 sm:w-auto sm:min-w-128 sm:px-14"
    >
      <LanguageIcon className="auth-locale-icon flex-shrink-0" size="sm" />
      <span className="hidden max-w-128 truncate sm:inline">
        {localeName || localeCode.toUpperCase()}
      </span>
      <KeyboardArrowDownIcon
        className="auth-locale-chevron hidden flex-shrink-0 sm:block"
        size="sm"
      />
    </Button>
  ) : (
    <Button
      disabled={changeLocale.isPending}
      className="capitalize"
      startIcon={<LanguageIcon />}
      endIcon={<KeyboardArrowDownIcon />}
    >
      {localeName}
    </Button>
  );

  return (
    <MenuTrigger
      floatingWidth={isAuthAppearance ? 'auto' : 'matchTrigger'}
      placement={isAuthAppearance ? 'bottom-end' : undefined}
      offset={isAuthAppearance ? 8 : undefined}
      selectionMode="single"
      selectedValue={localeCode}
      onSelectionChange={value => {
        const newLocale = value as string;
        if (newLocale !== localeCode) {
          changeLocale.mutate({locale: newLocale});
        }
      }}
    >
      {trigger}
      <Menu>
        {siteLocales.map(locale => (
          <MenuItem
            value={locale.language}
            key={locale.language}
            className="capitalize"
          >
            {locale.name}
          </MenuItem>
        ))}
      </Menu>
    </MenuTrigger>
  );
}
